import { useMemo } from 'react';
import { BidHistoryItem, BidHistoryStats } from '@/types/bidHistory';
import { subDays, isAfter, isBefore, startOfDay } from 'date-fns';

interface UseBidHistoryStatsProps {
  bids: BidHistoryItem[];
  allBids: BidHistoryItem[]; // Unfiltered bids for trend calculation
  dateRange: {
    start: Date;
    end: Date;
  };
}

export function useBidHistoryStats({ bids, allBids, dateRange }: UseBidHistoryStatsProps): BidHistoryStats {
  return useMemo(() => {
    // Current period stats
    const totalBids = bids.length;
    const wonBids = bids.filter(b => b.outcome === 'won').length;
    const lostBids = bids.filter(b => b.outcome === 'lost').length;
    const pendingBids = bids.filter(b => b.outcome === 'pending').length;
    
    // Win rate: won / (won + lost) - excludes pending and expired
    const resolvedBids = wonBids + lostBids;
    const winRate = resolvedBids > 0 ? (wonBids / resolvedBids) * 100 : 0;

    // Total won value
    const totalWonValueCents = bids
      .filter(b => b.outcome === 'won' && b.awardAmountCents)
      .reduce((sum, b) => sum + (b.awardAmountCents || 0), 0);

    // Average bid value
    const avgBidValueCents = totalBids > 0
      ? bids.reduce((sum, b) => sum + b.totalBidCents, 0) / totalBids
      : 0;

    // Average response time
    const avgResponseTimeHours = totalBids > 0
      ? bids.reduce((sum, b) => sum + b.responseTimeHours, 0) / totalBids
      : 0;

    // Calculate previous period for trends
    const periodDays = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const previousPeriodStart = startOfDay(subDays(dateRange.start, periodDays));
    const previousPeriodEnd = startOfDay(dateRange.start);

    // Filter bids from previous period
    const previousBids = allBids.filter(bid => {
      const bidDate = new Date(bid.submittedAt);
      return isAfter(bidDate, previousPeriodStart) && isBefore(bidDate, previousPeriodEnd);
    });

    const prevTotalBids = previousBids.length;
    const prevWonBids = previousBids.filter(b => b.outcome === 'won').length;
    const prevLostBids = previousBids.filter(b => b.outcome === 'lost').length;
    const prevResolvedBids = prevWonBids + prevLostBids;
    const prevWinRate = prevResolvedBids > 0 ? (prevWonBids / prevResolvedBids) * 100 : 0;
    const prevTotalWonValueCents = previousBids
      .filter(b => b.outcome === 'won' && b.awardAmountCents)
      .reduce((sum, b) => sum + (b.awardAmountCents || 0), 0);

    // Calculate percentage changes
    const calculateChange = (current: number, previous: number): number => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      totalBids,
      wonBids,
      lostBids,
      pendingBids,
      winRate,
      totalWonValueCents,
      avgBidValueCents,
      avgResponseTimeHours,
      trends: {
        totalBidsChange: calculateChange(totalBids, prevTotalBids),
        wonBidsChange: calculateChange(wonBids, prevWonBids),
        winRateChange: winRate - prevWinRate, // Absolute change for percentages
        totalWonValueChange: calculateChange(totalWonValueCents, prevTotalWonValueCents),
      },
    };
  }, [bids, allBids, dateRange]);
}
